import { useEffect, useRef, useState } from "react";
import { usePostHog } from "posthog-js/react";
import { Link, useFetcher } from "react-router";
import { PartnerMerchantDetails } from "~/types/application/partnerMerchantDetails";
import Button from "@components/atoms/Button";
import VerificationCodeInput from "@components/forms/VerificationCodeInput";
import { AUTH_ROUTES, CARD_DETAILS_ROUTE } from "@constants/routes";
import { useOnMount } from "@hooks/useOnMount";
import { useSubmitEmailMfaCode } from "@hooks/useSubmitEmailMfaCode";
import GoogleLogo from "@icons/GoogleLogo";
import ReshopLogoSvg from "@icons/ReshopLogo";

export interface EmailCodeMfaCredentials {
  email: string;
  twoFactorCodeToken: string;
}

interface EmailVerificationSectionProps {
  email: string;
  credentials: EmailCodeMfaCredentials | null;
  redirectTo: string | null;
  googleAuthEnabled: boolean;
  isIframe: boolean;
  merchant?: PartnerMerchantDetails | null;
}

export default function EmailVerificationSection({
  email,
  credentials,
  redirectTo,
  googleAuthEnabled,
  isIframe,
  merchant,
}: EmailVerificationSectionProps) {
  const fetcher = useFetcher();
  const {
    onSubmitEmailMfaCode,
    loadingEmailMfaCode,
    emailMfaCodeError,
    setEmailMfaCodeError,
  } = useSubmitEmailMfaCode();
  const [code, setCode] = useState("");
  const [hasSubmitted, setHasSubmitted] = useState(false);
  const [resendingCode, setResendingCode] = useState(false);
  const [isRedirectingToGoogle, setIsRedirectingToGoogle] = useState(false);
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);
  const posthog = usePostHog();
  const postHogDistinctId = posthog.get_distinct_id();

  useOnMount(() => {
    posthog.capture("code_only_login_page_view");
  });

  useEffect(() => {
    if (fetcher.state === "idle") {
      setResendingCode(false);
    }
  }, [fetcher.state]);

  useEffect(() => {
    if (!credentials) return;
    const submitCode = async () => {
      posthog.capture("email_mfa_code_submitted", {
        email: credentials.email,
      });
      setHasSubmitted(true);
      await onSubmitEmailMfaCode({
        code,
        token: credentials.twoFactorCodeToken,
        email: credentials.email,
        postHogDistinctId,
        redirectTo,
      });
    };

    if (code.length === 6 && !hasSubmitted) {
      submitCode();
    }
  }, [
    code,
    credentials,
    hasSubmitted,
    onSubmitEmailMfaCode,
    redirectTo,
    postHogDistinctId,
    posthog,
  ]);

  const handleResendCode = async () => {
    setResendingCode(true);
    const data = new FormData();
    data.append("email", email);
    await fetcher.submit(data, {
      action: AUTH_ROUTES.LOGIN,
      method: "post",
    });
  };

  const handleCodeChange = (value: string) => {
    setCode(value);
    setHasSubmitted(false);
    setEmailMfaCodeError(null);
  };

  const handleCodeComplete = (value: string) => {
    setCode(value);
  };

  const handleGoogleAuthClick = () => {
    setIsRedirectingToGoogle(true);

    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }

    timeoutRef.current = setTimeout(() => {
      setIsRedirectingToGoogle(false);
    }, 3000);
  };

  const isCardDetailsRedirect = redirectTo === CARD_DETAILS_ROUTE;
  const oauthInitializeUrl = redirectTo
    ? `/oauth/google/initialize?redirect_to=${encodeURIComponent(redirectTo)}`
    : "/oauth/google/initialize";

  return (
    <div className="flex flex-col gap-6">
      <div className="flex flex-col gap-2">
        {merchant && merchant.logoUrl && (
          <div className="flex flex-row items-center h-16 relative mb-2">
            <div className="absolute overflow-hidden left-0 h-16 w-16 rounded-full border-2 border-gray-100 bg-white flex items-center justify-center z-10">
              <img
                src={merchant.logoUrl}
                alt={`${merchant.name} logo`}
                className="h-full w-full object-cover"
              />
            </div>
            <div className="absolute overflow-hidden left-14 h-16 w-16 rounded-full border-2 border-gray-100 bg-white flex items-center justify-center z-20">
              <ReshopLogoSvg />
            </div>
          </div>
        )}
        <h1 className="header1ExtraBold">
          {isCardDetailsRedirect ? (
            "Verify your email to view your Reshop Card"
          ) : merchant ? (
            `${merchant.name} partners with Reshop to issue instant refunds`
          ) : (
            <>
              <div>Confirm your order to</div>
              <div>unlock your instant refund</div>
            </>
          )}
        </h1>
        <p className="bodySmall text-darkGrey">
          We sent a verification code to <strong>{email}</strong>
        </p>
      </div>
      <VerificationCodeInput
        onChange={handleCodeChange}
        onComplete={handleCodeComplete}
        disabled={loadingEmailMfaCode}
        focusOnMount
      />

      <div className="flex flex-col gap-4">
        <span
          role="button"
          tabIndex={0}
          onClick={handleResendCode}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              handleResendCode();
            }
          }}
          className="underline cursor-pointer bodySmall text-darkGrey text-center"
        >
          {resendingCode ? "Resending..." : "Resend code"}
        </span>

        {emailMfaCodeError && (
          <p className="bodySmall text-center text-red-700">
            {emailMfaCodeError}
          </p>
        )}
      </div>

      {googleAuthEnabled && !isIframe && (
        <div className="w-full">
          <Link to={oauthInitializeUrl}>
            <Button
              type="button"
              leftIcon={isRedirectingToGoogle ? null : <GoogleLogo />}
              onClick={handleGoogleAuthClick}
              loading={isRedirectingToGoogle}
              disabled={isRedirectingToGoogle}
              text="Verify with Google instead"
              theme="white"
              textTheme="black"
              className="w-full border py-3"
            />
          </Link>
        </div>
      )}
    </div>
  );
}
